/*
 * Promo popup (Events) - IT Geeks fix 09/10/2026
 * - Never steals focus when it appears (it used to close the header search and menus mid-use).
 * - Waits while the visitor is typing / using a menu, drawer or search, then shows.
 * - Close always works: no longer depends only on the exit animation finishing
 *   (it never fired with iOS Reduce Motion, so the popup stayed open and the X just turned into +).
 * - Events button only appears after the popup has fully closed (no overlap), and is shown on
 *   later page loads once the popup has been dismissed, so it can always be reopened.
 * - Frequency setting is respected on every device (the old pagehide reset made it reappear on every page).
 */
if (!customElements.get('promo-popup-component')) {
  class PromoPopupComponent extends HTMLElement {
    connectedCallback() {
      this._popup = this.querySelector('.promo-popup');
      this._fab = this.querySelector('.promo-popup-fab');
      this._closeBtn = this.querySelector('.promo-popup__close');

      if (!this._popup) return;
      if (this.dataset.enabled !== 'true') return;

      this._frequency = this.dataset.frequency || 'once_per_session';
      this._storageKey = 'rikumo_promo_' + this.dataset.sectionId;
      const delay = parseInt(this.dataset.delay || '3', 10) * 1000;

      /* Entrance animation class (only plays when the popup becomes visible) */
      this._popup.classList.add('promo-popup--anim-' + (this.dataset.animation || 'slide_up'));

      /* Event listeners */
      /* Delegated on the component so the X works wherever inside it the tap lands (icon, path, padding) */
      this.addEventListener('click', (e) => {
        if (!e.target.closest('.promo-popup__close')) return;
        e.preventDefault();
        e.stopPropagation();
        this._closePopup();
      });
      this._fab?.addEventListener('click', () => this._reopenPopup());
      this._popup.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this._closePopup();
      });

      const redirectUrl = (this.dataset.redirectUrl || '').trim();
      if (redirectUrl) {
        this._popup.style.cursor = 'pointer';
        this._popup.addEventListener('click', (e) => {
          if (e.target.closest('.promo-popup__close, a, button')) return;
          window.location.href = redirectUrl;
        });
      }

      this._onSectionUnload = (e) => {
        if (e.detail && e.detail.sectionId === this.dataset.sectionId) clearTimeout(this._timer);
      };
      document.addEventListener('shopify:section:unload', this._onSectionUnload);

      /* Already dismissed: keep the popup closed, but offer the Events button to reopen it */
      if (this._wasDismissed()) {
        this._hidePopup();
        this._showFab();
        return;
      }

      this._timer = setTimeout(() => this._showWhenIdle(), delay);
    }

    disconnectedCallback() {
      clearTimeout(this._timer);
      clearTimeout(this._exitFallback);
      if (this._onSectionUnload) document.removeEventListener('shopify:section:unload', this._onSectionUnload);
    }

    /* ---- Frequency / storage ---- */
    _storage() {
      return this._frequency === 'once_ever' ? window.localStorage : window.sessionStorage;
    }

    _wasDismissed() {
      if (this._frequency === 'every_visit') return false;
      try {
        return !!this._storage().getItem(this._storageKey);
      } catch (err) {
        return false;
      }
    }

    _rememberDismissed() {
      if (this._frequency === 'every_visit') return;
      try {
        this._storage().setItem(this._storageKey, '1');
      } catch (err) {
        /* storage blocked (e.g. private mode) - ignore */
      }
    }

    /* ---- Don't interrupt the visitor ---- */
    _isUserBusy() {
      const active = document.activeElement;
      if (active && active !== document.body && active.matches('input, textarea, select, [contenteditable="true"]')) return true;
      if (document.querySelector('.section-header details[open], header details[open], menu-drawer details[open], details-modal details[open], cart-drawer.active')) return true;
      if (/(^|\s)overflow-hidden/.test(document.body.className)) return true;
      return false;
    }

    _showWhenIdle() {
      if (this._isUserBusy()) {
        this._timer = setTimeout(() => this._showWhenIdle(), 1500);
        return;
      }
      this._showPopup();
    }

    /* ---- Show / hide ---- */
    _showPopup() {
      /* Intentionally no focus() here - moving focus closed the search and menus */
      this._closing = false;
      this._popup.classList.remove('promo-popup--exiting');
      this._popup.setAttribute('aria-hidden', 'false');
    }

    _hidePopup() {
      this._popup.classList.remove('promo-popup--exiting');
      this._popup.setAttribute('aria-hidden', 'true');
    }

    _showFab() {
      if (this.dataset.showFab === 'true' && this._fab) this._fab.setAttribute('aria-hidden', 'false');
    }

    _hideFab() {
      if (this._fab) this._fab.setAttribute('aria-hidden', 'true');
    }

    _closePopup() {
      if (this._closing || this._popup.getAttribute('aria-hidden') === 'true') return;
      this._closing = true;
      clearTimeout(this._timer);
      this._rememberDismissed();

      const finish = () => {
        if (!this._closing) return;
        this._closing = false;
        clearTimeout(this._exitFallback);
        this._hidePopup();
        this._showFab();
      };

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        finish();
        return;
      }

      this._popup.classList.add('promo-popup--exiting');
      this._popup.addEventListener('animationend', finish, { once: true });
      /* Safety net: close even if the exit animation never reports back */
      this._exitFallback = setTimeout(finish, 400);
    }

    _reopenPopup() {
      clearTimeout(this._exitFallback);
      this._hideFab();
      this._showPopup();
      /* The visitor asked for it, so moving focus to the popup is fine here */
      this._closeBtn?.focus({ preventScroll: true });
    }
  }

  customElements.define('promo-popup-component', PromoPopupComponent);
}
